import React, { useState, useEffect } from "react";
import { ArrowLeft, Save } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { ApiResponse } from "../types/common";
import { settingsRelatedApi, usersApi } from "../utils/api";

interface CoachNotificationData {
  newMessages: boolean;
  newSubscriptionSold: boolean;
  newPackageSold: boolean;
  newVideoSubmitted: boolean;
}

export function CoachNotificationSettings() {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState<CoachNotificationData>({
    newMessages: true,
    newSubscriptionSold: true,
    newPackageSold: true,
    newVideoSubmitted: true
  });

  // Simulate API call to fetch notification settings
  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true);
      try {
        const response: ApiResponse = await settingsRelatedApi.getCoachSettings();
      
        if (response.success) {
          setSettings(response.data || []);
        } else {
          throw new Error(response.message || 'Failed to load user stats');
        }
        
        // Settings are already set in state with default values
      } catch (error) {
        console.error("Failed to fetch coach notification settings:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Simulate API call to save settings
      // const response = await fetch("host/api/v1/coach-notification-settings", {
      //   method: 'PUT',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     Authorization: `Bearer ${localStorage.getItem("authToken")}`
      //   },
      //   body: JSON.stringify(settings)
      // });

      console.log("Saving coach notification settings:", settings);
      
      // Mock success
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // You could show a success toast here
    } catch (error) {
      console.error("Failed to save coach notification settings:", error);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="space-y-4">
          <div className="h-8 bg-muted animate-pulse rounded" />
          <div className="h-4 bg-muted animate-pulse rounded w-1/3" />
          <div className="space-y-2">
            {Array.from({ length: 4 }, (_, i) => (
              <div key={i} className="h-12 bg-muted animate-pulse rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => window.history.back()}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
        </Button>
        <div>
          <h1>Edit</h1>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="space-y-6">
        {/* New Messages */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="newMessages"
                checked={settings.newMessages}
                onCheckedChange={(checked) => 
                  setSettings(prev => ({ ...prev, newMessages: !!checked }))
                }
              />
              <Label htmlFor="newMessages">
                New Messages
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* New Subscription Sold */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="newSubscriptionSold"
                checked={settings.newSubscriptionSold}
                onCheckedChange={(checked) => 
                  setSettings(prev => ({ ...prev, newSubscriptionSold: !!checked }))
                }
              />
              <Label htmlFor="newSubscriptionSold">
                New Subscription Sold
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* New Package Sold */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="newPackageSold"
                checked={settings.newPackageSold}
                onCheckedChange={(checked) => 
                  setSettings(prev => ({ ...prev, newPackageSold: !!checked }))
                }
              />
              <Label htmlFor="newPackageSold">
                New Package Sold
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* New Video Submitted */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="newVideoSubmitted"
                checked={settings.newVideoSubmitted}
                onCheckedChange={(checked) => 
                  setSettings(prev => ({ ...prev, newVideoSubmitted: !!checked }))
                }
              />
              <Label htmlFor="newVideoSubmitted">
                New Video Submitted
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-center pt-8">
          <Button 
            onClick={handleSave}
            disabled={isSaving}
            className="bg-blue-500 hover:bg-blue-600 text-white min-w-[120px] px-8"
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}